import Big from 'big.js';

import { type TaxMode } from 'types/tax-rate';
import {
  systemDefaultValues,
  type Invoice,
  type SendInvoicePdfEmailParams,
  type GetInvoiceEmailContentParams,
  type Item,
  type TaxRate,
  getSalesItemPrice,
  formatWithCurrency,
  formatMoneyAmount
} from '@a1/shared';
import callAzureFunction from './call-azure-function';
import handleError from './handle-error';
import callAzureFunctionPublic from './call-azure-function-public';
import type { ButtonProps } from '@mui/material/Button';
import { enqueueSnackbar } from 'notistack';

// project components
import DescriptionIcon from '@mui/icons-material/Description';
import AwaitingPaymentIcon from '@mui/icons-material/Payment';
import PaidIcon from '@mui/icons-material/CheckCircle';
import VoidIcon from '@mui/icons-material/Cancel';
import RefundedIcon from '@mui/icons-material/Replay';
import PendingIcon from '@mui/icons-material/Autorenew';

const InvoiceIcons = {
  DRAFT: DescriptionIcon,
  AWAITING_PAYMENT: AwaitingPaymentIcon,
  PAID: PaidIcon,
  VOID: VoidIcon,
  REFUNDED: RefundedIcon,
  PENDING: PendingIcon
};

const RE_POSITIVE_DECIMAL_2 = /^(?!.*\..*\.)[0-9]*(\.[0-9]{0,2})?$/;
const RE_SIGNED_DECIMAL_2 = /^-?(?!.*\..*\.)[0-9]*(\.[0-9]{0,2})?$/;

/**
 * Checks if the given value is a valid tax rate.
 * @returns `true` or `false`
 */
const isValidTaxRate = (value: string) => RE_POSITIVE_DECIMAL_2.test(value);

/**
 * Checks if the given value is a valid sales price for an item.
 * @returns `true` or `false`
 */
const isValidSalesPrice = (value: string) => RE_POSITIVE_DECIMAL_2.test(value);

const isValidLineItemPrice = (value: string) => RE_SIGNED_DECIMAL_2.test(value);

/**
 * Checks if the given value is a valid unit amount for an item.
 * @returns `true` or `false`
 */
const isValidUnitAmount = (value: string) => RE_POSITIVE_DECIMAL_2.test(value);

/**
 * Checks if the given value is a valid quantity
 * @returns `true` or `false`
 */
const isValidQuantity = (value: string) => RE_POSITIVE_DECIMAL_2.test(value);

const taxModes: Record<string, TaxMode> = {
  EXCLUSIVE: {
    id: 'EXCLUSIVE',
    label: 'Tax exclusive'
  },
  INCLUSIVE: {
    id: 'INCLUSIVE',
    label: 'Tax inclusive'
  },
  NO_TAX: {
    id: 'NO_TAX',
    label: 'No tax'
  }
};

interface TaxTotal {
  total: number;
  taxRate?: TaxRate;
}

const getTaxAmount = ({ price, taxRate, taxMode }: { price?: Big.BigSource | null; taxRate?: TaxRate | null; taxMode?: string | null }) => {
  const basePrice = new Big(price ?? 0);

  if (!taxRate?.total || !taxMode || taxMode === taxModes.NO_TAX.id) {
    return new Big(0);
  }

  const taxRateBig = new Big(taxRate.total).div(100);

  if (taxMode === taxModes.INCLUSIVE.id) {
    // For inclusive tax: tax = price - (price / (1 + taxRate))
    return basePrice.minus(basePrice.div(new Big(1).plus(taxRateBig))).round(2);
  }
  // For exclusive tax: tax = price * taxRate
  return basePrice.times(taxRateBig).round(2);
};

const getPriceBeforeTax = ({
  price,
  taxRate,
  taxMode
}: {
  price?: Big.BigSource | null;
  taxRate?: TaxRate | null;
  taxMode?: string | null;
}) => {
  const basePrice = new Big(price ?? 0);

  if (!taxRate?.total || !taxMode || taxMode !== taxModes.INCLUSIVE.id) {
    // For exclusive or no tax, price before tax is the same as price
    return basePrice;
  }

  // For inclusive tax: priceBeforeTax = price / (1 + taxRate)
  const taxRateBig = new Big(taxRate.total).div(100);
  return basePrice.div(new Big(1).plus(taxRateBig)).round(2);
};

const getSalesItemTaxAmount = (item: Item | null, billingType: string | null) => {
  const price = getSalesItemPrice(item, billingType);
  const taxRate = item?.SalesTaxRate;
  const taxMode = item?.salesTaxMode;
  return getTaxAmount({ price, taxRate, taxMode });
};

const getSalesItemPriceBeforeTax = (item: Item | null, billingType: string | null) => {
  const price = getSalesItemPrice(item, billingType);
  const taxRate = item?.SalesTaxRate;
  const taxMode = item?.salesTaxMode;
  return getPriceBeforeTax({ price, taxRate, taxMode });
};

const getCurrencyName = (currency: string) => {
  const displayNames = new Intl.DisplayNames([systemDefaultValues.locale], { type: 'currency' });
  return displayNames.of(currency.toUpperCase());
};

// To deprecate, use PdfSender instead
const sendInvoicePdfEmail = async ({
  emails,
  subject,
  html,
  pdfBlob,
  invoiceNumber,
  invoiceId,
  invoiceToken,
  onSuccess,
  onError
}: SendInvoicePdfEmailParams) => {
  try {
    const formData = new FormData();
    formData.append('emails', emails.join(','));
    formData.append('subject', subject);
    formData.append('html', html);
    formData.append('pdfFile', pdfBlob, `${invoiceNumber ? invoiceNumber : `INV-${invoiceId}`}.pdf`);
    formData.append('invoiceId', invoiceId ?? '');

    let data: string;

    if (invoiceToken) {
      const response = await callAzureFunctionPublic<string>({
        url: 'public/send-pdf',
        method: 'post',
        data: formData,
        headers: {
          Authorization: `Bearer ${invoiceToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      data = response.data;
    } else {
      const response = await callAzureFunction<string>({
        url: 'send-pdf',
        method: 'post',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      data = response.data;
    }

    onSuccess?.(data);
    return data;
  } catch (error) {
    const message = handleError(error);
    onError?.(message);
    throw error;
  }
};

const getInvoiceEmailContent = ({
  firstName,
  fullName,
  bookingId,
  invoiceId,
  bookingEntityName,
  isAddOnCharge
}: GetInvoiceEmailContentParams) => {
  if (isAddOnCharge) {
    const subject = `Additional Charge for ${bookingEntityName} #[${bookingId}] - ${fullName}`;
    const emailBody = `
      <p>Dear ${firstName},</p>
      <p>A new charge has been added to your booking. Please find the invoice INV-${invoiceId} for this charge attached.</p>
    `;
    return { subject, emailBody };
  }

  const subject = `Invoice for ${bookingEntityName} #[${bookingId}] - ${fullName}`;
  const emailBody = `
    <p>Dear ${firstName},</p>
    <p>Thank you for your booking. Please find attached invoice INV-${invoiceId} for your booking #${bookingId}.</p> 
  `;

  return { subject, emailBody };
};

const getOnlineButtonProps = (
  willPayOnline: boolean
): {
  variant: ButtonProps['variant'];
  color: ButtonProps['color'];
} => ({
  variant: willPayOnline ? 'contained' : 'outlined',
  color: willPayOnline ? 'secondary' : 'primary'
});

const getInvoiceOnlyButtonProps = (
  willPayOnline: boolean
): {
  variant: ButtonProps['variant'];
  color: ButtonProps['color'];
} => ({
  variant: !willPayOnline ? 'contained' : 'outlined',
  color: !willPayOnline ? 'secondary' : 'primary'
});

const validateInvoice = ({
  invoice,
  xeroEnabled,
  requireBillToContact
}: {
  invoice: Invoice;
  xeroEnabled?: boolean;
  requireBillToContact?: boolean;
}) => {
  const errors: {
    invoiceNumber?: string;
    billToId?: string;
    missingQuantity?: boolean;
    missingPrice?: boolean;
  } = {};

  // Check for empty required fields
  if (!invoice.invoiceNumber) {
    errors.invoiceNumber = 'Invoice number is required';
  }

  if (!invoice.billToId && xeroEnabled) {
    errors.billToId = 'Bill To Contact is required';
  } else if (!invoice.billToId && requireBillToContact) {
    errors.billToId = 'Bill To Contact is required to approve this invoice';
  }

  // Validate each section's items have required fields with correct types
  invoice.InvoiceSections?.forEach((section) => {
    section.InvoiceSectionItems?.forEach((item) => {
      if (item.quantity === undefined || item.quantity === null || item.quantity === '') {
        errors.missingQuantity = true;
      }

      if (item.price === undefined || item.price === null || item.price === '') {
        errors.missingPrice = true;
      }
    });
  });

  if (errors.missingQuantity) {
    enqueueSnackbar({
      variant: 'error',
      message: 'Some items are missing a quantity value',
      anchorOrigin: { vertical: 'top', horizontal: 'right' }
    });
  }

  if (errors.missingPrice) {
    enqueueSnackbar({
      variant: 'error',
      message: 'Some items are missing a valid price value',
      anchorOrigin: { vertical: 'top', horizontal: 'right' }
    });
  }

  return {
    isValid: !errors.invoiceNumber && !errors.billToId && !errors.missingQuantity && !errors.missingPrice,
    errors
  };
};

export {
  isValidTaxRate,
  isValidSalesPrice,
  isValidUnitAmount,
  isValidQuantity,
  isValidLineItemPrice,
  validateInvoice,
  taxModes,
  getSalesItemTaxAmount,
  getSalesItemPriceBeforeTax,
  getCurrencyName,
  formatWithCurrency,
  formatMoneyAmount,
  sendInvoicePdfEmail,
  getInvoiceEmailContent,
  getOnlineButtonProps,
  getInvoiceOnlyButtonProps,
  type TaxTotal,
  InvoiceIcons
};
